/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
*/

import {
    Component, Input,
    OnInit, TemplateRef
} from '@angular/core';
import {Toolbar, ToolbarItem, ToolbarDropdown, TextSizeDropdown} from '../../types';
import Editor from '../../Editor';
import {MenuService} from './menu.service';

export const DEFAULT_TOOLBAR: Toolbar = [
    ['bold', 'italic'],
    ['code', 'blockquote'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list'],
    [{heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6']}],
    ['link', 'image'],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify']
];

export const TOOLBAR_MINIMAL: Toolbar = [
    ['bold', 'italic'],
    [{heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6']}],
    ['link', 'image'],
    ['text_color', 'background_color']
];

const DEFAULT_COLOR_PRESETS = [
    '#128474',
    '#004750',
    '#94d60a',
    '#cedb00',
    '#60c3d6',
    '#178fd0',
    '#632b86',
    '#f0e1b6',
    '#6a696d',
    '#575756',
    '#9d9d9c',
    '#c6c6c6',
    
    '#88c1b9',
    '#7fa3a7',
    '#c9ea84',
    '#e6ed7f',
    '#afe1ea',
    '#8bc7e7',
    '#b195c2',
    '#f7f0da',
    '#b4b4b6',
    '#ababaa',
    '#cececd',
    '#e2e2e2'
];

/**
 * Menu component.
 */
@Component({
    selector: 'fc-ag-editor-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.scss'],
    providers: [MenuService]
})

export class MenuComponent implements OnInit {
    /**
     * Toolbar.
     */
    @Input() toolbar: Toolbar = TOOLBAR_MINIMAL;

    /**
     * Color list.
     */
    @Input() colorPresets: string[] = DEFAULT_COLOR_PRESETS;

    /**
     * Disabled.
     */
    @Input() disabled = false;

    /**
     * Editor instance.
     */
    @Input() editor: Editor;

    /**
     * Custom menu reference.
     */
    @Input() customMenuRef: TemplateRef<any> | null = null;

    /**
     * Dropdown placement.
     */
    @Input() dropdownPlacement: 'top' | 'bottom' = 'bottom';

    /**
     * Toggle commands.
     */
    toggleCommands: any[] = [
        'bold', 'italic',
        'underline', 'strike',
        'code', 'blockquote',
        'ordered_list', 'bullet_list',
        'align_left', 'align_center', 'align_right', 'align_justify'
    ];

    /**
     * Icon container class.
     */
    iconContainerClass = ['FcAgEditor__MenuItem', 'FcAgEditor__MenuItem--Icon'];

    /**
     * Download container class.
     */
    dropdownContainerClass = ['FcAgEditor__Dropdown'];

    /**
     * Seperator Class.
     */
    seperatorClass = ['FcAgEditor__Seperator'];

    /**
     * Default constructor.
     * 
     * @param menuService Menu service.
     */
    constructor(private menuService: MenuService) {
    }

    /**
     * Getter for color lists.
     * 
     * @returns Array.
     */
    get presets(): string[][] {
        const col = 12;
        const colors: string[][] = [];

        this.colorPresets.forEach((color, index) => {
            const row = Math.floor(index / col);

            if (!colors[row]) {
                colors.push([]);
            }

            colors[row].push(color);
        });

        return colors;
    }

    /**
     * Track by index.
     * 
     * @param index Number.
     * @returns Number.
     */
    trackByIndex(index: number): number {
        return index;
    }

    /**
     * Is dropdown command.
     * 
     * @param item Toolbar item.
     * @returns Boolean.
     */
    isDropDown(item: ToolbarItem): boolean {
        if ((item as ToolbarDropdown)?.heading) {
            return true;
        }

        if ((item as TextSizeDropdown)?.fontGroup) {
            return true;
        }
        
        return false;
    }

    /**
     * Get dropdown items.
     * 
     * @param item Toolbar item.
     * @returns ToolbarDropdown.
     */
    getDropdownItems(item: ToolbarItem): ToolbarDropdown {
        return item as ToolbarDropdown;
    }

    /**
     * Angular life cycle.
     * 
     * @throws Error.
     */
    ngOnInit(): void {
        if (!this.editor) {
            throw new Error('FcAgEditor: Required editor instance');
        }

        this.menuService.editor = this.editor;
    }
}
